import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GameResultTeamLobby from "../../components/GameBoard/GameResultTeamLobby";
import GameResultBox from "../../components/GameBoard/GameResultBox";
import { getGameDetailsAPI } from "../../api/game";
import { useQuery } from "@tanstack/react-query";
import Loader from "../../components/Loader/Loader";
import useToast from "../../hooks/useToast";
import { useTranslation } from "react-i18next";
import PrimaryBtn from "../../components/button/PrimaryBtn";
import SecondaryBtn from "../../components/button/SecondaryBtn";
import ConfirmPlayAgainModal from "../../components/Modal/ConfirmPlayAgainModal";

interface Participant {
  _id: string;
  team_id: string;
  full_name: string;
  email: string;
  total_points: number;
  gender: string | null;
  profile_image?: string | null;
  display_category_name?: { en: string; ar: string } | null;
  display_achievement_name?: { en: string; ar: string } | null;
  user_id?: {
    _id: string;
    full_name: string;
    email: string;
    is_guest?: boolean;
    IQ?: number | null;
    IQ_old?: number | null;
    profile_image?: string | null;
    display_category_name?: { en: string; ar: string } | null;
    display_achievement_name?: { en: string; ar: string } | null;
  };
  correct_answer_count: number;
  IQ?: number | null;
  IQ_old?: number | null;
}

interface Lifeline {
  lifeline_id: {
    _id: string;
    name: { en: string; ar: string };
    description: { en: string; ar: string };
    image: string;
  };
  is_used: boolean;
  _id: string;
}

interface Team {
  _id: string;
  game_id: string;
  team_name: string;
  team_number: number;
  total_points: number;
  lifelines: Lifeline[];
  participants: Participant[];
  correct_answer_count: number;
}

interface Question {
  _id: string;
  game_id: string;
  game_category_id: string;
  question_id: {
    question: { en: string; ar: string };
    answer: { en: string; ar: string };
    _id: string;
    question_type: string;
    difficulty: string;
    time_limit: number;
  };
  answered_by: string | null;
}

interface Category {
  _id: string;
  game_id: string;
  category_id: {
    name: { en: string; ar: string };
    description: { en: string; ar: string };
    _id: string;
    image: string;
  };
  questions: Question[];
}

interface GameData {
  _id: string;
  game_name: string;
  number_of_teams: number;
  status: string;
  winner_team_id: string | null;
  host_id: {
    _id: string;
    full_name: string;
    email: string;
  };
  teams: Team[];
  categories: Category[];
}

const GameResult = () => {
  const navigate = useNavigate();
  const { gameId } = useParams<{ gameId: string }>();
  const { t, i18n } = useTranslation();
  const [isPlayAgainModalOpen, setIsPlayAgainModalOpen] = useState(false);
  const [pendingLastGameTemplate, setPendingLastGameTemplate] = useState<any>(null);

  // Block back navigation to game board after game over/pause
  useEffect(() => {
    const handlePopState = () => {
      // Push the current state again to prevent going back
      window.history.pushState(null, "", window.location.href);
      // Optionally navigate to my-games instead
      navigate("/my-games", { replace: true });
    };

    // Push initial state to history
    window.history.pushState(null, "", window.location.href);

    // Listen for back button
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  const {
    data: gameDetails,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["gameDetails", gameId],
    queryFn: () => getGameDetailsAPI(gameId as string),
    enabled: !!gameId,
  });

  const gameData: GameData | undefined = gameDetails?.data?.game;

  const winnerTeam = gameData?.teams?.find((team) => team._id === gameData?.winner_team_id);

  const lobbyTeams = gameData?.teams
    ? [...gameData.teams].sort((a, b) => (a.team_number ?? 0) - (b.team_number ?? 0))
    : [];

  const sortedTeams = gameData?.teams ? [...gameData.teams].sort((a, b) => b.total_points - a.total_points) : [];

  const totalQuestions =
    gameData?.categories?.reduce((acc, category) => {
      return acc + (category.questions?.length || 0);
    }, 0) || 0;

  const allQuestions = gameData?.categories?.flatMap((category) => category.questions || []) || [];

  const getAnswersGivenByUser = (userId: string) => {
    return allQuestions.filter((q) => q.answered_by === userId).length;
  };

  const getTeamStats = (team: Team) => {
    const participantsWithStats = (team.participants || []).map((participant) => ({
      _id: participant._id,
      full_name: participant.full_name,
      email: participant.email,
      total_points: participant.total_points || 0,
      answers_given: getAnswersGivenByUser(participant.user_id?._id || participant._id),
      correct_answer_count: participant.correct_answer_count || 0,
      is_guest: participant.user_id?.is_guest === true,
      IQ: participant.IQ ?? participant.user_id?.IQ ?? null,
      IQ_old: participant.IQ_old ?? participant.user_id?.IQ_old ?? null,
    }));

    return {
      total: totalQuestions,
      correct: team.correct_answer_count || 0,
      participants: participantsWithStats,
    };
  };

  const handleCreateNewGame = () => {
    if (!gameData) {
      navigate("/create-game");
      return;
    }

    const template = {
      game_name: gameData.game_name,
      number_of_teams: gameData.teams?.length ?? 0,
      teams:
        gameData.teams?.map((team, teamIndex) => ({
          team_name: team.team_name,
          lifelines: (team.lifelines || [])
            .map((l: any) => {
              // Support multiple backend shapes and always keep only lifeline id strings.
              if (typeof l === "string") return l;
              if (typeof l?.lifeline_id === "string") return l.lifeline_id;
              if (typeof l?.lifeline_id?._id === "string") return l.lifeline_id._id;
              if (typeof l?._id === "string") return l._id;
              if (typeof l?.id === "string") return l.id;
              return null;
            })
            .filter((id): id is string => Boolean(id)),
          participants: (team.participants || []).map((p, participantIndex: number) => ({
            // Frontend needs a stable numeric id for rendering/removal
            // IMPORTANT: do not start from 0, because CreateGame UI checks `if (participant?.id)`
            id: teamIndex * 1000 + participantIndex + 1,
            user_id: p?.user_id?._id ?? null,
            full_name: p?.full_name ?? "",
            email: p?.email ?? "",
            phone_number: undefined,
            country_code: undefined,
            gender: p?.gender ?? null,
            profile_image: p?.profile_image ?? p?.user_id?.profile_image ?? null,
            IQ: p?.IQ ?? p?.user_id?.IQ ?? null,
            joinStatus: "joined",
            isFromAPI: true,
            invitedAt: new Date().toISOString(),
            apiFieldsFilled: {
              full_name: true,
              email: !!p?.email,
              phone_number: false,
              country_code: false,
            },
          })),
        })) ?? [],
    };

    setPendingLastGameTemplate(template);
    setIsPlayAgainModalOpen(true);
  };

  const handleConfirmPlayAgain = () => {
    if (!pendingLastGameTemplate) {
      setIsPlayAgainModalOpen(false);
      return;
    }

    navigate("/create-game", { state: { lastGameTemplate: pendingLastGameTemplate } });
    setIsPlayAgainModalOpen(false);
    setPendingLastGameTemplate(null);
  };

  const isTeamWinner = (teamId: string) => !!gameData?.winner_team_id && gameData?.winner_team_id === teamId;

  return (
    <>
      <div
        className="wrapper flex-1 flex flex-col bg-no-repeat xl:bg-[length:100%_100%] bg-cover bg-bottom bg-primary-ivory"
      // style={{
      //   backgroundImage: `url(${GamePlayImg})`,
      //   backgroundColor: "#B9F2FD",
      // }}
      >
        <section className="w-full flex justify-center items-center relative flex-1 2xl:pb-[1vw] lg:pb-5 pb-2">
          <div className="w-full h-full  flex justify-center items-center 2xl:py-[1.67vw] md:py-3 py-3 landscape:max-lg:py-2 2xl:pt-[8vw] xl:pt-25 pt-17 ">
            <div className="w-full 2xl:max-w-[91.667vw] max-w-[86rem] mx-auto 2xl:px-[0.833vw] xl:px-10 px-4 relative z-20 h-full 2xl:pt-[1vw] xl:pt-3 pt-2 flex flex-col justify-center md:mt-4">
              {isLoading || isFetching ? (
                <Loader />
              ) : (
                <>
                  <div className="Ques-part w-full 2xl:rounded-[1.04vw] rounded-2xl 2xl:p-[1.04vw] xl:p-1 p-1 flex 2xl:gap-[1vw] xl:gap-4 md:gap-3 gap-2 overflow-hidden sm:flex-nowrap flex-wrap">
                    <div className="w-full 2xl:rounded-[1.04vw] rounded-2xl 2xl:p-[1.25vw] p-3 overflow-hidden h-full mt-10 md:mt-30 xl:mt-4">
                      <div className="w-full mx-auto flex flex-col items-stretch justify-start h-full">
                        {gameData?.winner_team_id ? (
                          <h6 className="font-garamond font-bold 2xl:text-[1.25vw]/snug md:text-2xl/snug text-lg/snug text-primary-rose 2xl:mb-[0.75vw] text-center">
                            {t("gameResult.congratulations")}{" "}
                            <span className="text-primary-orange">
                              {winnerTeam?.team_name || t("gameResult.winner")}{" "}
                            </span>
                            {t("gameResult.youveScored")}{" "}
                            <span className="bg-primary-orange text-white 2xl:px-[0.41vw] 2xl:py-[0.20vw] py-1 inline-block px-2 skew-x-[-10deg] rounded-md font-semibold font-garamond 2xl:text-[1vw]/snug text-base/snug font-bold text-[#413938]">
                              {winnerTeam?.total_points || 0}
                            </span>{" "}
                            {t("gameResult.points")}
                          </h6>
                        ) : (
                          <div className="text-center 2xl:mb-[0.75vw] mb-3">
                            <h6 className="font-garamond font-bold 2xl:text-[1.25vw]/snug md:text-2xl/snug text-lg/snug text-[#413938] 2xl:mb-[0.1vw] mb-0">
                              {t("gameResult.itsATie")}
                            </h6>
                            <p className="2xl:text-[1vw]/snug xl:text-base/snug text-sm/tight font-bold text-[#413938] text-center">
                              {t("gameResult.noWinnerThisTime")}
                            </p>
                          </div>
                        )}
                        <GameResultTeamLobby
                          teams={lobbyTeams}
                          winnerTeamId={gameData?.winner_team_id ?? null}
                          language={i18n.language}
                        />
                        <div className="flex 2xl:gap-[1vw] gap-2.5 2xl:mt-[1.25vw] mt-24 w-full justify-center flex-row max-sm:[zoom:0.7] shrink-0">
                          <PrimaryBtn
                            className="!mb-0 2xl:max-w-[15.021vw] lg:max-w-[300px] max-w-[250px]"
                            onClick={handleCreateNewGame}
                          >
                            {t("gameResult.playAgain")}
                          </PrimaryBtn>
                          <SecondaryBtn
                            className="!mb-0 2xl:max-w-[15.021vw] lg:max-w-[300px] max-w-[250px]"
                            onClick={() => navigate("/create-game")}
                          >
                            {t("gameResult.gotohomepage")}
                          </SecondaryBtn>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="2xl:pb-[1vw] lg:pb-5 pb-2">
          <div className="w-full 2xl:max-w-[91.667vw] max-w-[86rem] mx-auto 2xl:px-[0.833vw] xl:px-10 px-4">
            <div className="flex flex-wrap justify-center 2xl:gap-[1.25vw] xl:gap-5 md:gap-3 gap-2.5">
              {!isLoading &&
                !isFetching &&
                sortedTeams.map((team) => {
                  const stats = getTeamStats(team);
                  const isTeamWinner = team._id === gameData?.winner_team_id;
                  return (
                    <div
                      key={team._id}
                      className="w-full xs:w-[calc(50%-0.375rem)] sm:w-[calc(25%-1rem)] lg:w-[calc(25%-1.125rem)] 2xl:w-[calc(25%-0.9375vw)]"
                    >
                      {isTeamWinner && (
                        <div className="-skew-x-10 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-orange 2xl:px-[0.5vw] px-2 2xl:py-[0.2vw] py-0.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] z-10 text-center w-full mx-auto mb-3">
                          <span className="skew-x-10 2xl:text-[1vw] text-sm font-bold text-white">🏆 {t("gameResultBox.winner")}</span>
                        </div>
                      )}
                      <GameResultBox
                        BtnText={team.team_name}
                        score={team.total_points.toString()}
                        TotalRun={stats.total.toString()}
                        CurrentRun={stats.correct.toString()}
                        isCorrect={isTeamWinner}
                        participants={stats.participants}
                      />
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      </div>

      <ConfirmPlayAgainModal
        isOpen={isPlayAgainModalOpen}
        onClose={() => {
          setIsPlayAgainModalOpen(false);
          setPendingLastGameTemplate(null);
        }}
        onConfirm={handleConfirmPlayAgain}
      />
    </>
  );
};

export default GameResult;
